// axiosInstance, not the cachios client — see enrollments.js
import {axiosInstance as http} from "@/http_client";

/**
 * Dashboard feed: the cross-cutting view the API assembles for the home
 * page (attention items, what to continue, conference windows, light plan
 * summary, professional-development rollup).
 *
 * Course/conference ROWS stay in the enrollments/registrations modules —
 * the feed only names them by id — so the dashboard tiles can never drift
 * from the Enrollments and Conference Records pages.
 *
 * Always fetched with {force: true}: the dashboard is the landing page after
 * external actions (signing at TPO, finishing a course) and a 2-minute-stale
 * cache copy would show the alert the student just cleared.
 */
export default {
    state: {
        feed: null,
        feedLoading: false,
        feedError: null,
    },
    getters: {
        dashboardFeed: state => state.feed,
        dashboardLoading: state => state.feedLoading,
        dashboardError: state => state.feedError,
        attentionItems: state => (state.feed && state.feed.attention) || [],
        dashboardConferences: state => (state.feed && state.feed.conferences) || null,
        dashboardPayment: state => (state.feed && state.feed.payment) || null,
        dashboardPd: state => (state.feed && state.feed.pd) || null,

        // Feed "continue" entries merged onto the store's enrollment rows.
        // While the feed is still loading, fall back to the Enrollments
        // page's own ordering (most recently started first) so the section
        // paints immediately.
        continueEnrollments: (state, getters, rootState) => {
            const rows = rootState.enrollments.enrollments || [];
            const active = rows.filter(e => !e.completed_at && !e.reactivated_to_enrollment_id && !e.is_expired);
            const feed = state.feed && Array.isArray(state.feed.continue) ? state.feed.continue : null;
            if (!feed) {
                return active
                    .slice()
                    .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
                    .slice(0, 4)
                    .map(e => ({...e, next_step: null}));
            }
            const byId = new Map(active.map(e => [e.id, e]));
            return feed
                .map(item => {
                    const row = byId.get(item.enrollment_id);
                    return row ? {...row, last_activity_at: item.last_activity_at, next_step: item.next_step, route: item.route} : null;
                })
                .filter(Boolean);
        },
        activeEnrollmentCount: (state, getters, rootState) => {
            const rows = rootState.enrollments.enrollments || [];
            return rows.filter(e => !e.completed_at && !e.reactivated_to_enrollment_id && !e.is_expired).length;
        },
    },
    mutations: {
        setDashboardFeed(state, feed) {
            state.feed = feed;
            state.feedError = null;
        },
        setDashboardLoading(state, loading) {
            state.feedLoading = loading;
        },
        setDashboardError(state, err) {
            state.feedError = err;
        },
    },
    actions: {
        getDashboard({commit, rootGetters}) {
            // Discard a response that arrives after the user changed (portal
            // login-as-user relay) — see enrollments.js
            const forUser = rootGetters.currentUser && rootGetters.currentUser.id;
            commit('setDashboardLoading', true);
            return http.get('app/dashboard').then(response => {
                const nowUser = rootGetters.currentUser && rootGetters.currentUser.id;
                if (nowUser !== forUser) return null;
                commit('setDashboardFeed', response.data);
                return response.data;
            }).catch(err => {
                console.error('Error loading dashboard:', err);
                commit('setDashboardError', err);
                throw err;
            }).finally(() => {
                commit('setDashboardLoading', false);
            });
        },
    }
};
